<script>
    import FormImageUpload from '@fno/component2/form/InputFile.svelte'
    import FormColorPicker from '@fno/component2/form/InputColor.svelte'
    import FormSavingButtons from '../FormSavingButtons.svelte'
    import FormAlerts from '../FormAlerts.svelte'
    import { createEventDispatcher } from 'svelte'
    import { postPublicFile } from '@fno/service/api/files'
    import { Row, Col } from 'sveltestrap'

    export let context
    export let saving
    export let previewColor

    // control whether or not we need to compose the icon from logo and bgcolor
    $: composeIcon = !context.form.clientOnboarding.attributes.steps.APP_ICONOGRAPHY.launchIcon.key

    $: previewColor = `#${context.form.clientOnboarding.attributes.steps.APP_ICONOGRAPHY.backgroundColor}`
    $: previewLogo =
        context.form.clientOnboarding.attributes.steps.APP_ICONOGRAPHY.launchIconLogo.key &&
        `https://${context.form.clientOnboarding.attributes.steps.APP_ICONOGRAPHY.launchIconLogo.bucket}/${context.form.clientOnboarding.attributes.steps.APP_ICONOGRAPHY.launchIconLogo.key}`
    $: launchIcon =
        context.form.clientOnboarding.attributes.steps.APP_ICONOGRAPHY.launchIcon.key &&
        `https://${context.form.clientOnboarding.attributes.steps.APP_ICONOGRAPHY.launchIcon.bucket}/${context.form.clientOnboarding.attributes.steps.APP_ICONOGRAPHY.launchIcon.key}`
    $: primaryLogo =
        context.form.clientOnboarding.attributes.steps.APP_ICONOGRAPHY.primaryAppLogo.key &&
        `https://${context.form.clientOnboarding.attributes.steps.APP_ICONOGRAPHY.primaryAppLogo.bucket}/${context.form.clientOnboarding.attributes.steps.APP_ICONOGRAPHY.primaryAppLogo.key}`

    const dispatch = createEventDispatcher()
    const updatePreviewColor = (event) => {
        previewColor = `#${event.detail.value}`
        dispatch('change', event.detail)
    }
</script>

<style>
    .fno-launch-icon-preview {
        width: 150px;
        height: 150px;
    }

    .fno-launch-icon {
        width: 112px;
        height: 112px;
        object-fit: scale-down;
    }

    .ios {
        border-radius: 17.5%;
    }

    .android {
        border-radius: 50%;
    }
</style>

<h1>App Icons</h1>

<p>
    This icon is the main icon that will be used for your school throughout the system. It will be seen in both the
    online Portal and the mobile applications.
</p>

<Row>
    <Col xs="6">
        <FormImageUpload
            label="Primary App Logo"
            helptext="The primary logo will appear in locations such as the affiliations menu and filter menu. Recommended file type and size: 500px x 500px, .png"
            {context}
            formKey="clientOnboarding"
            keypath="attributes.steps.APP_ICONOGRAPHY.primaryAppLogo"
            bind:value={context.form.clientOnboarding.attributes.steps.APP_ICONOGRAPHY.primaryAppLogo}
            fileUploader={postPublicFile}
            on:change
        />
    </Col>
    <Col class="d-flex flex-column justify-content-center align-items-center">
        <h4>Preview</h4>

        {#if primaryLogo}<img class="fno-launch-icon" src={primaryLogo} alt="preview of primary logo" />{/if}
    </Col>
</Row>

<h2>Launch Icon</h2>

<p>
    Upload your own high-quality finished launch icon using the “select a file” button on the right. Or, upload your
    launch icon and select the launch icon background color using the color picker below, by clicking on a color or
    typing in a corresponding hex value.
</p>

<Row>
    <Col>
        <Row>
            <Col>
                <FormImageUpload
                    label="Launch Icon"
                    helptext="Finished launch icon to be used, as-is. Recommended file type and size: 1024px x 1024px, .png"
                    {context}
                    formKey="clientOnboarding"
                    keypath="attributes.steps.APP_ICONOGRAPHY.launchIcon"
                    bind:value={context.form.clientOnboarding.attributes.steps.APP_ICONOGRAPHY.launchIcon}
                    fileUploader={postPublicFile}
                    on:change
                />
            </Col>
        </Row>

        <hr />

        <Row class="mb-5">
            <Col>
                <FormImageUpload
                    label="Launch Icon Logo"
                    helptext="Transparent icon to be used with a background color to generate a launch icon. Recommended file type and size: 512 x 512px, .png"
                    {context}
                    formKey="clientOnboarding"
                    keypath="attributes.steps.APP_ICONOGRAPHY.launchIconLogo"
                    bind:value={context.form.clientOnboarding.attributes.steps.APP_ICONOGRAPHY.launchIconLogo}
                    fileUploader={postPublicFile}
                    on:change
                />
            </Col>
        </Row>

        <Row>
            <Col class="d-flex justify-content-center">
                <FormColorPicker
                    {context}
                    formKey="clientOnboarding"
                    keypath="attributes.steps.APP_ICONOGRAPHY.backgroundColor"
                    on:change={(event) => updatePreviewColor(event)}
                    bind:value={context.form.clientOnboarding.attributes.steps.APP_ICONOGRAPHY.backgroundColor}
                />
            </Col>
        </Row>
    </Col>

    <Col class="d-flex flex-column align-items-center">
        <h4>Previews</h4>

        {#if composeIcon}
            <h5>iOS</h5>
            <div
                class="fno-launch-icon-preview ios d-flex justify-content-center align-items-center"
                style={`background-color: ${previewColor};`}
            >
                {#if previewLogo}
                    <img class="fno-launch-icon" src={previewLogo} alt="iOS preview logo on background color" />
                {/if}
            </div>
            <br>
            <h5>Android</h5>
            <div
                    class="fno-launch-icon-preview android d-flex justify-content-center align-items-center"
                    style={`background-color: ${previewColor};`}
            >
                {#if previewLogo}
                    <img class="fno-launch-icon" src={previewLogo} alt="Android preview logo on background color" />
                {/if}
            </div>
        {:else}
            <h5>iOS</h5>
            <img class="fno-launch-icon-preview ios" src={launchIcon} alt="pre-finished launch icon" />
            <br>
            <h5>Android</h5>
            <img class="fno-launch-icon-preview android" src={launchIcon} alt="pre-finished launch icon" />
        {/if}
    </Col>
</Row>

<FormAlerts bind:alerts={context.alerts} />

<FormSavingButtons {context} {saving} on:save on:nextStepClicked on:previousStepClicked />
